/**
 * Store themes — 50 distinct palettes the store admin can pick from.
 *
 * Each theme has a single anchor brand hex + secondary hex + surface base + ink.
 * A small HSL ramp generator expands the brand anchor into a 50-900 palette,
 * which is then applied on the StoreView wrapper via CSS variables.
 */

// ────────────────────────── color helpers ──────────────────────────

const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const num = parseInt(full, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const rgbToHex = ({ r, g, b }) => {
    const to = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
};

const rgbToHsl = ({ r, g, b }) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0, s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default:
        }
        h /= 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToRgb = ({ h, s, l }) => {
    h /= 360; s /= 100; l /= 100;
    if (s === 0) {
        const v = l * 255;
        return { r: v, g: v, b: v };
    }
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return {
        r: hue2rgb(p, q, h + 1 / 3) * 255,
        g: hue2rgb(p, q, h) * 255,
        b: hue2rgb(p, q, h - 1 / 3) * 255,
    };
};

const adjustL = (hex, targetL, satFactor = 1) => {
    const hsl = rgbToHsl(hexToRgb(hex));
    return rgbToHex(hslToRgb({ h: hsl.h, s: Math.min(100, hsl.s * satFactor), l: targetL }));
};

/**
 * Generate a 10-step brand ramp (50..900) from a single anchor hex.
 * The anchor lives at step 500. Lighter steps desaturate slightly, darker
 * steps deepen slightly — produces palettes that feel coherent.
 */
const ramp = (anchor) => {
    const stops = [
        { k: 50,  l: 96, s: 0.45 },
        { k: 100, l: 92, s: 0.55 },
        { k: 200, l: 84, s: 0.70 },
        { k: 300, l: 72, s: 0.85 },
        { k: 400, l: 60, s: 0.95 },
        { k: 500, l: null, s: 1.0 },
        { k: 600, l: 38, s: 1.05 },
        { k: 700, l: 28, s: 1.10 },
        { k: 800, l: 20, s: 1.10 },
        { k: 900, l: 14, s: 1.10 },
    ];
    const out = {};
    stops.forEach(({ k, l, s }) => {
        out[k] = l === null ? anchor : adjustL(anchor, l, s);
    });
    out[500] = anchor;
    return out;
};

// ────────────────────────── theme definitions ──────────────────────────
// Each theme: { id, name (Hebrew), category, mood, brand, secondary, surfaceBase, surface, ink, inkOnDark? }
//
// `brand` is the anchor hex (becomes shade 500). `secondary` is one hex.
// `surfaceBase` is the page background (warmer/cooler off-white or moody dark).
// `surface` is the card background. `ink` is the primary text color.
// If `dark: true`, the theme renders with a dark surface and light ink automatically.

const RAW_THEMES = [
    // ─── PREMIUM HIGH SOCIETY (1–10) ───
    { id: 'royal-velvet',       name: 'קטיפה מלכותית',     category: 'premium',  brand: '#7a1530', secondary: '#c9a66b', surfaceBase: '#fdf6f2', surface: '#ffffff', ink: '#1a0810' },
    { id: 'onyx-couture',       name: 'אוניקס קוטור',      category: 'premium',  brand: '#c9a35a', secondary: '#7a6033', surfaceBase: '#15130f', surface: '#1d1a14', ink: '#f5ecd6', dark: true },
    { id: 'champagne-marble',   name: 'שיש שמפניה',        category: 'premium',  brand: '#b89968', secondary: '#7a6240', surfaceBase: '#fbf6ec', surface: '#ffffff', ink: '#2a2419' },
    { id: 'platinum-suite',     name: 'סוויטת פלטינום',    category: 'premium',  brand: '#8a96a3', secondary: '#c0c5cc', surfaceBase: '#f7f8fa', surface: '#ffffff', ink: '#1a1f26' },
    { id: 'midnight-sapphire',  name: 'ספיר חצות',         category: 'premium',  brand: '#1d3a6e', secondary: '#3b6db5', surfaceBase: '#f3f5fa', surface: '#ffffff', ink: '#0d1530' },
    { id: 'emerald-library',    name: 'ספריית האזמרגד',    category: 'premium',  brand: '#0f5c3e', secondary: '#b8945a', surfaceBase: '#f1f6f1', surface: '#fbfaf3', ink: '#0a2316' },
    { id: 'parisian-boudoir',   name: 'בודואר פריזאי',     category: 'premium',  brand: '#b86a7a', secondary: '#d6b896', surfaceBase: '#fbf4f4', surface: '#ffffff', ink: '#2b1622' },
    { id: 'manhattan-penthouse',name: 'פנטהאוז מנהטן',     category: 'premium',  brand: '#3a3a3a', secondary: '#b07a4a', surfaceBase: '#f5f3ef', surface: '#ffffff', ink: '#141414' },
    { id: 'hermes-saddle',      name: 'אוכף קלאסי',        category: 'premium',  brand: '#9c5a2f', secondary: '#e0c9a6', surfaceBase: '#faf3e8', surface: '#ffffff', ink: '#2a160a' },
    { id: 'tuscan-marble',      name: 'שיש טוסקני',        category: 'premium',  brand: '#c08a5a', secondary: '#8a6a44', surfaceBase: '#fbf5ea', surface: '#ffffff', ink: '#2a1c10' },

    // ─── ELEGANT / SOPHISTICATED (11–20) ───
    { id: 'mediterranean-linen',name: 'פשתן ים תיכוני',    category: 'elegant',  brand: '#2e6a96', secondary: '#e6c87a', surfaceBase: '#f6f9fb', surface: '#ffffff', ink: '#13283b' },
    { id: 'provence-lavender',  name: 'לבנדר פרובנס',      category: 'elegant',  brand: '#8a7ab8', secondary: '#b0c490', surfaceBase: '#f7f4fa', surface: '#ffffff', ink: '#221b35' },
    { id: 'english-garden',     name: 'גן אנגלי',          category: 'elegant',  brand: '#6e8a5c', secondary: '#c98a96', surfaceBase: '#f5f7f1', surface: '#ffffff', ink: '#1b2616' },
    { id: 'japanese-tea-house', name: 'בית תה יפני',       category: 'elegant',  brand: '#4a5a6a', secondary: '#a07a52', surfaceBase: '#f4f3ef', surface: '#fbfaf6', ink: '#1c2128' },
    { id: 'scandinavian-snow',  name: 'שלג סקנדינבי',      category: 'elegant',  brand: '#7a8fa6', secondary: '#d4b27a', surfaceBase: '#f8f9fb', surface: '#ffffff', ink: '#1d2632' },
    { id: 'modern-tokyo',       name: 'טוקיו מודרני',      category: 'elegant',  brand: '#2a2a2a', secondary: '#c83a3a', surfaceBase: '#fafafa', surface: '#ffffff', ink: '#111111' },
    { id: 'cherry-blossom',     name: 'פריחת דובדבן',      category: 'elegant',  brand: '#e09bb4', secondary: '#a8c89a', surfaceBase: '#fef6f7', surface: '#ffffff', ink: '#2e1721' },
    { id: 'coastal-cape',       name: 'כף החוף',           category: 'elegant',  brand: '#1f3b5c', secondary: '#e8b87a', surfaceBase: '#f4f7fb', surface: '#ffffff', ink: '#0e1c2e' },
    { id: 'vintage-bookshop',   name: 'חנות ספרים וינטג׳', category: 'elegant',  brand: '#7a5230', secondary: '#c9a66b', surfaceBase: '#f7f0e2', surface: '#fdf9ef', ink: '#2a1a0c' },
    { id: 'black-forest',       name: 'יער שחור',          category: 'elegant',  brand: '#2a4a36', secondary: '#c9a66b', surfaceBase: '#f4f6f2', surface: '#fbfaf3', ink: '#0f1c14' },

    // ─── WARM & COZY (21–30) ───
    { id: 'autumn-harvest',     name: 'קציר סתיו',         category: 'warm',     brand: '#b75a2a', secondary: '#7a8a3a', surfaceBase: '#fbf4ea', surface: '#fffaf0', ink: '#2a1408' },
    { id: 'pumpkin-spice',      name: 'דלעת מתובלת',       category: 'warm',     brand: '#c97a2a', secondary: '#7a4a2a', surfaceBase: '#fbf2e4', surface: '#fff8ed', ink: '#2a1a0a' },
    { id: 'maple-sunrise',      name: 'זריחת מייפל',       category: 'warm',     brand: '#d09030', secondary: '#a8523a', surfaceBase: '#fdf4e3', surface: '#fffaf0', ink: '#2c1a08' },
    { id: 'honey-hive',         name: 'כוורת דבש',         category: 'warm',     brand: '#d8a72a', secondary: '#a86a2a', surfaceBase: '#fdf5da', surface: '#fffaeb', ink: '#2c1f08' },
    { id: 'cinnamon-bun',       name: 'קינמון',            category: 'warm',     brand: '#a8542a', secondary: '#d8b87a', surfaceBase: '#faf1e3', surface: '#fff7ec', ink: '#2a1408' },
    { id: 'apple-orchard',      name: 'מטע תפוחים',        category: 'warm',     brand: '#c4382e', secondary: '#5c7e3a', surfaceBase: '#fbf2ee', surface: '#fffaf6', ink: '#2c0e09' },
    { id: 'hearth-stone',       name: 'אבן האח',           category: 'warm',     brand: '#8a5a3e', secondary: '#c45a3e', surfaceBase: '#f7efe5', surface: '#fffaf2', ink: '#27170c' },
    { id: 'coffee-bean',        name: 'פולי קפה',          category: 'warm',     brand: '#5a3a1e', secondary: '#c9a66b', surfaceBase: '#f6efe2', surface: '#fffaef', ink: '#1f1408' },
    { id: 'toffee-cream',       name: 'טופי שמנת',         category: 'warm',     brand: '#a8702e', secondary: '#d8c094', surfaceBase: '#fbf4e8', surface: '#fffaee', ink: '#2a1a0a' },
    { id: 'spice-market',       name: 'שוק התבלינים',      category: 'warm',     brand: '#c44e2a', secondary: '#d8a02a', surfaceBase: '#fbf0e4', surface: '#fff8ec', ink: '#2a1208' },

    // ─── NATURE / EARTHY (31–40) ───
    { id: 'olive-grove',        name: 'חורש זיתים',        category: 'nature',   brand: '#8B9A6B', secondary: '#C9A66B', surfaceBase: '#FAF8F5', surface: '#FFFFFF', ink: '#1F1D1A' },
    { id: 'sage-meadow',        name: 'אחו מרווה',         category: 'nature',   brand: '#7e9a82', secondary: '#d4b894', surfaceBase: '#f5f7f3', surface: '#fdfcf6', ink: '#1e2a1f' },
    { id: 'forest-cabin',       name: 'בקתת היער',         category: 'nature',   brand: '#3e6a4a', secondary: '#a87a4a', surfaceBase: '#f3f5f1', surface: '#fbfaf3', ink: '#0f2014' },
    { id: 'mountain-lodge',     name: 'בקתת הר',           category: 'nature',   brand: '#5a6a7a', secondary: '#d8a03a', surfaceBase: '#f5f6f8', surface: '#fdfcf8', ink: '#1c2128' },
    { id: 'desert-bloom',       name: 'פריחת המדבר',       category: 'nature',   brand: '#c4946a', secondary: '#7e9a72', surfaceBase: '#faf2e6', surface: '#fffaef', ink: '#2c1c0e' },
    { id: 'driftwood-beach',    name: 'חוף הסחף',          category: 'nature',   brand: '#9a7e5a', secondary: '#6aa89a', surfaceBase: '#f6f3ec', surface: '#fdfaf2', ink: '#241a0c' },
    { id: 'sea-salt',           name: 'מלח הים',           category: 'nature',   brand: '#6aa89a', secondary: '#d4b894', surfaceBase: '#f1f7f5', surface: '#fcfaf3', ink: '#0e2622' },
    { id: 'tropical-jungle',    name: 'ג׳ונגל טרופי',      category: 'nature',   brand: '#0e7a4a', secondary: '#c8d030', surfaceBase: '#f0f7f1', surface: '#fbfaf3', ink: '#0a2014' },
    { id: 'caribbean-lagoon',   name: 'לגונה קריבית',      category: 'nature',   brand: '#1e9aa6', secondary: '#f08a5a', surfaceBase: '#f0f8f9', surface: '#fdfcf6', ink: '#0a2628' },
    { id: 'bamboo-zen',         name: 'במבוק זן',          category: 'nature',   brand: '#7a9e5a', secondary: '#d8c8a0', surfaceBase: '#f4f6ee', surface: '#fdfbf2', ink: '#1c2410' },

    // ─── BOHEMIAN / HIPPIE (41–50) ───
    { id: 'bohemian-sunset',    name: 'שקיעה בוהמית',      category: 'hippie',   brand: '#d4582e', secondary: '#7a3a6a', surfaceBase: '#faf0e6', surface: '#fffaef', ink: '#2a0e1a' },
    { id: 'patchouli-dreams',   name: 'חלומות פצ׳ולי',     category: 'hippie',   brand: '#8a4a8a', secondary: '#8a6a3a', surfaceBase: '#f5f0f7', surface: '#fcfaf3', ink: '#220e2a' },
    { id: 'wildflower-meadow',  name: 'אחו פרחי בר',       category: 'hippie',   brand: '#c97a9a', secondary: '#d8b85a', surfaceBase: '#fbf4f0', surface: '#fffaef', ink: '#2c1622' },
    { id: 'woodstock',          name: 'וודסטוק',           category: 'hippie',   brand: '#d8b82a', secondary: '#5c8a3a', surfaceBase: '#f9f6e0', surface: '#fffce6', ink: '#221e08' },
    { id: 'tie-dye',            name: 'טאי-דאי',           category: 'hippie',   brand: '#c4388a', secondary: '#1ea8a8', surfaceBase: '#fbf0f5', surface: '#fffaf6', ink: '#2a0e22' },
    { id: 'festival-earth',     name: 'אדמת פסטיבל',       category: 'hippie',   brand: '#a8542e', secondary: '#7a4a2a', surfaceBase: '#f7eddf', surface: '#fef7e6', ink: '#28140a' },
    { id: 'sunflower-commune',  name: 'קומונת חמניות',     category: 'hippie',   brand: '#e8b830', secondary: '#7a4a1e', surfaceBase: '#fdf7d6', surface: '#fffce6', ink: '#28200a' },
    { id: 'crystal-healing',    name: 'ריפוי בקריסטלים',   category: 'hippie',   brand: '#b890c4', secondary: '#e09bb4', surfaceBase: '#f7f0fa', surface: '#fdfaf6', ink: '#26142e' },
    { id: 'indigo-tribe',       name: 'שבט אינדיגו',       category: 'hippie',   brand: '#3a4a9a', secondary: '#d8a03a', surfaceBase: '#f0f1fa', surface: '#fbfaf3', ink: '#0e1430' },
    { id: 'earth-mama',         name: 'אדמת אמא',          category: 'hippie',   brand: '#a85a3a', secondary: '#5a7a3a', surfaceBase: '#f7ede2', surface: '#fef7e6', ink: '#291208' },
];

// ────────────────────────── token expansion ──────────────────────────

const tintTowards = (base, target, t) => {
    const a = hexToRgb(base), b = hexToRgb(target);
    return rgbToHex({
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    });
};

/** Expand a raw theme spec into the full CSS-variable token map. */
const expandTheme = (spec) => {
    const brand = ramp(spec.brand);
    const secondary500 = spec.secondary;
    const secondary600 = adjustL(spec.secondary, 38, 1.0);
    const surfaceBase = spec.surfaceBase;
    const surface = spec.surface;
    // surface-muted: surface base tinted slightly toward ink
    const surfaceMuted = tintTowards(surfaceBase, spec.ink, 0.06);
    const surfaceSunken = tintTowards(surfaceBase, spec.ink, 0.10);
    const surfaceInverse = spec.dark ? surface : '#1F1D1A';

    const ink = spec.ink;
    const inkStrong = tintTowards(ink, spec.dark ? '#ffffff' : '#000000', 0.18);
    const inkMuted = tintTowards(ink, surfaceBase, 0.45);
    const inkSubtle = tintTowards(ink, surfaceBase, 0.62);
    const inkFaint = tintTowards(ink, surfaceBase, 0.78);
    const inkInverse = spec.dark ? ink : '#FFFFFF';

    const line = tintTowards(surfaceBase, ink, 0.14);
    const lineSubtle = tintTowards(surfaceBase, ink, 0.08);
    const lineStrong = tintTowards(surfaceBase, ink, 0.22);

    return {
        ...spec,
        tokens: {
            'brand-50':  brand[50],
            'brand-100': brand[100],
            'brand-200': brand[200],
            'brand-300': brand[300],
            'brand-400': brand[400],
            'brand-500': brand[500],
            'brand-600': brand[600],
            'brand-700': brand[700],
            'brand-800': brand[800],
            'brand-900': brand[900],
            'secondary-500': secondary500,
            'secondary-600': secondary600,
            'surface':        surface,
            'surface-base':   surfaceBase,
            'surface-muted':  surfaceMuted,
            'surface-sunken': surfaceSunken,
            'surface-inverse':surfaceInverse,
            'ink':         ink,
            'ink-strong':  inkStrong,
            'ink-muted':   inkMuted,
            'ink-subtle':  inkSubtle,
            'ink-faint':   inkFaint,
            'ink-inverse': inkInverse,
            'line':         line,
            'line-subtle':  lineSubtle,
            'line-strong':  lineStrong,
        },
    };
};

export const STORE_THEMES = RAW_THEMES.map(expandTheme);

export const DEFAULT_STORE_THEME_ID = 'olive-grove';

export const STORE_THEME_CATEGORIES = [
    { id: 'premium', label: 'יוקרה וחברה גבוהה' },
    { id: 'elegant', label: 'אלגנטיות עידנית' },
    { id: 'warm',    label: 'חמים וביתי' },
    { id: 'nature',  label: 'טבע ואדמה' },
    { id: 'hippie',  label: 'בוהמיינים וטבעוניים' },
];

export const findStoreTheme = (id) =>
    STORE_THEMES.find(t => t.id === id) || STORE_THEMES.find(t => t.id === DEFAULT_STORE_THEME_ID);
